import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Settings,
  Lock,
  Briefcase,
  Image,
  PenSquare,
  Star,
  HelpCircle,
  Images,
  ChevronDown,
  ChevronRight,
  X,
  Menu,
  MonitorPlay,
  MessageSquareQuote,
  FolderTree,
  Users,
  ShoppingBag,
  Tag,
  Building2,
  Landmark,
  Shield,
  CalendarClock,
} from "lucide-react";
import api from "../../api/axios";

const menuItems = [
  { id: "dashboard", title: "Dashboard", icon: LayoutDashboard, path: "/" },
   { id: "settings", title: "Site Ayarları", icon: Settings, path: "/settings" },
  {
    id: "change-password",
    title: "Şifre Değiştir",
    icon: Lock,
    path: "/change-password",
  },
  {
    id: "appointments",
    title: "Randevular",
    icon: CalendarClock,
    alwaysVisible: true,
    subMenu: [
      { title: "Randevu Listesi", path: "/appointment-list" },
      { title: "Uygunluk & Takvim", path: "/availability" },
    ],
  },
  {
    id: "services",
    title: "Hizmetler",
    icon: Briefcase,
    subMenu: [
      { title: "Hizmet Listesi", path: "/service-list" },
      { title: "Yeni Hizmet Ekle", path: "/add-service" },
    ],
  },
  {
    id: "service-categories",
    title: "Hizmet Kategorileri",
    icon: FolderTree,
    subMenu: [
      { title: "Kategori Listesi", path: "/service-category-list" },
      { title: "Yeni Kategori Ekle", path: "/add-service-category" },
    ],
  },
  { id: "logo", title: "Logo", icon: Image, path: "/logo" },
  {
    id: "banner",
    title: "Banner",
    icon: MonitorPlay,
    subMenu: [
      { title: "Banner Listesi", path: "/banner-list" },
      { title: "Banner Ekle", path: "/add-banner" },
    ],
  },
  {
    id: "blog",
    title: "Blog",
    icon: PenSquare,
    subMenu: [
      { title: "Blog Listesi", path: "/blog-list" },
      { title: "Blog Ekle", path: "/blog-add" },
    ],
  },
  {
    id: "referans",
    title: "Referanslar",
    icon: Star,
    subMenu: [
      { title: "Referans Ekle", path: "/add-reference" },
      { title: "Referans Listesi", path: "/reference-list" },
    ],
  },
  {
    id: "contracted-banks",
    title: "Anlaşmalı Bankalar",
    icon: Landmark,
    subMenu: [
      { title: "Banka Listesi", path: "/contracted-bank-list" },
      { title: "Banka Ekle", path: "/add-contracted-bank" },
    ],
  },
  {
    id: "contracted-institutions",
    title: "Anlaşmalı Kurumlar",
    icon: Building2,
    subMenu: [
      { title: "Kurum Listesi", path: "/contracted-institution-list" },
      { title: "Kurum Ekle", path: "/add-contracted-institution" },
    ],
  },
  {
    id: "faq",
    title: "SSS",
    icon: HelpCircle,
    subMenu: [
      { title: "SSS Listesi", path: "/faq-list" },
      { title: "SSS Ekle", path: "/faq-add" },
    ],
  },
  {
    id: "galeri",
    title: "Galeri",
    icon: Images,
    subMenu: [
      { title: "Resim Ekle", path: "/upload-galeri" },
      { title: "Resim Listesi", path: "/galeri-list" },
      { title: "Etiket Yönetimi", path: "/galeri-tag-list" },
    ],
  },
  {
    id: "testimonial",
    title: "Yorumlar",
    icon: MessageSquareQuote,
    subMenu: [
      { title: "Yorum Listesi", path: "/testimonial-list" },
      { title: "Yorum Ekle", path: "/add-testimonial" },
    ],
  },
  {
    id: "employee",
    title: "Ekip",
    icon: Users,
    subMenu: [
      { title: "Ekip Listesi", path: "/employee-list" },
      { title: "Çalışan Ekle", path: "/add-employee" },
    ],
  },
  {
    id: "products",
    title: "Ürünler",
    icon: ShoppingBag,
    subMenu: [
      { title: "Ürün Listesi", path: "/product-list" },
      { title: "Yeni Ürün Ekle", path: "/add-product" },
    ],
  },
  {
    id: "product-categories",
    title: "Ürün Kategorileri",
    icon: Tag,
    subMenu: [
      { title: "Kategori Listesi", path: "/product-category-list" },
      { title: "Yeni Kategori Ekle", path: "/add-product-category" },
    ],
  },
];

export default function Sidebar({ isOpen, onToggle }) {
  const [openMenu, setOpenMenu] = useState(null);
  const [enabledModuleIds, setEnabledModuleIds] = useState(new Set());
  const [moduleOrders, setModuleOrders] = useState(new Map());
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const location = useLocation();

  // ModuleType enum (API) -> menuItem id mapping
  const moduleTypeToMenuId = {
    1: 'dashboard',
    2: 'settings',
    3: 'change-password',
    4: 'services',
    5: 'service-categories',
    6: 'logo',
    7: 'banner',
    8: 'blog',
    9: 'referans',
    10: 'contracted-banks',
    11: 'contracted-institutions',
    12: 'faq',
    13: 'galeri',
    14: 'testimonial',
    15: 'employee',
    16: 'products',
    17: 'product-categories'
  };

  // Modül yetkilerini yükle
  useEffect(() => {
    const fetchModulePermissions = async () => {
      try {
        const response = await api.get('/customermodulepermission/public');
        
        // Aktif modülleri filtrele
        const enabledModules = response.data
          .filter(p => p.isEnabled)
          .map(p => moduleTypeToMenuId[p.moduleType])
          .filter(id => id !== undefined);
        
        // OrderNumber bilgilerini al
        const orders = new Map();
        response.data.forEach(p => {
          const menuId = moduleTypeToMenuId[p.moduleType];
          if (menuId) {
            orders.set(menuId, p.orderNumber || 0);
          }
        });
        
        setEnabledModuleIds(new Set(enabledModules));
        setModuleOrders(orders);
        setPermissionsLoaded(true);
      } catch (error) {
        console.error('Modül yetkileri yüklenirken hata:', error);
        // Hata durumunda tüm modülleri göster
        const allIds = menuItems.map(item => item.id);
        setEnabledModuleIds(new Set(allIds));
        setPermissionsLoaded(true);
      }
    };

    fetchModulePermissions();
  }, []);

  // Sadece yetkili modülleri filtrele ve orderNumber'a göre sırala
  const visibleMenuItems = permissionsLoaded 
    ? menuItems
        .filter(item => item.alwaysVisible || enabledModuleIds.has(item.id))
        .sort((a, b) => {
          const orderA = moduleOrders.get(a.id) ?? 999;
          const orderB = moduleOrders.get(b.id) ?? 999;
          return orderA - orderB;
        })
    : [];

  const toggleMenu = (menu) => setOpenMenu(openMenu === menu ? null : menu);
  const isActive = (path) => location.pathname === path;

  const isSubMenuActive = (item) => {
    if (item.subMenu) {
      return item.subMenu.some((sub) => location.pathname === sub.path);
    }
    return false;
  };

  // Logo yerel olarak public/images klasöründen alınır
  const logoUrl = "/images/logo-white.png";

  return (
    <>
      <div
        className={`sidebar-overlay ${isOpen ? "active" : ""}`}
        onClick={onToggle}
      />
      <aside className={`sidebar ${isOpen ? "open" : "closed"}`}>
        <div className="sidebar-header">
          <Link to="/" className="sidebar-logo">
            <img 
              src={logoUrl} 
              alt="Logo" 
              className="sidebar-logo-img"
            />
          </Link>
          <button className="sidebar-close" onClick={onToggle}>
            <X size={20} />
          </button>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-section-title">MENÜ</div>
          {visibleMenuItems.map((item) => {
            const Icon = item.icon;
            const hasSubMenu = item.subMenu && item.subMenu.length > 0;
            const isMenuOpen = openMenu === item.id;
            const active = item.path
              ? isActive(item.path)
              : isSubMenuActive(item);

            return (
              <div key={item.id} className="nav-item-wrapper">
                {hasSubMenu ? (
                  <button
                    className={`nav-item ${active ? "active" : ""}`}
                    onClick={() => toggleMenu(item.id)}
                  >
                    <Icon size={20} className="nav-icon" />
                    <span className="nav-text">{item.title}</span>
                    {isMenuOpen ? (
                      <ChevronDown size={16} className="nav-arrow" />
                    ) : (
                      <ChevronRight size={16} className="nav-arrow" />
                    )}
                  </button>
                ) : (
                  <Link
                    to={item.path}
                    className={`nav-item ${active ? "active" : ""}`}
                  >
                    <Icon size={20} className="nav-icon" />
                    <span className="nav-text">{item.title}</span>
                  </Link>
                )}

                {hasSubMenu && (
                  <div className={`sub-menu ${isMenuOpen ? "open" : ""}`}>
                    {item.subMenu.map((sub) => (
                      <Link
                        key={sub.path}
                        to={sub.path}
                        className={`sub-menu-item ${isActive(sub.path) ? "active" : ""}`}
                      >
                        <span className="sub-dot" />
                        {sub.title}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-version">v2.0.0</div>
        </div>
      </aside>
    </>
  );
}
